/**
 * CONFIGURING · TESTING · MAPPING A SOURCE CONNECTION → HEBREW. 07 N-5, N-6, §2.3, §3.3, L-3, S-12.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * THE SISTER MODULE TO `sourceStatusText.ts`, AND THE DIVISION IS DELIBERATE.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * `sourceStatusText.ts` describes a connection AS IT IS — its state, its two watermarks, its
 * completeness. This one describes what happens when somebody tries to CHANGE it: configure it, test
 * it, discover what is behind it, bind one of its companies to one of ours.
 *
 * They stay apart because they answer to different rules. The first is a read that must never invent
 * a date; this one is a set of refusals that must never collapse — which is the Owner's direction
 * applied to the one surface where the product had, in a previous round's words, *"ZERO routes"*.
 *
 * ── THE FOUR DISTINCTIONS THAT MAY NOT BE FLATTENED ─────────────────────────────────────────────
 *
 * ONE — NOT CONFIGURED vs ERROR. Already carried by `sourceStatusText.ts`; the configuration refusal
 * here is the other side of it, and it names EVERY missing field rather than the first. 07 N-5: an
 * operator fixing one at a time is an operator who runs the probe five times, and a missing
 * `data_source_type` silently became a working default on 5 of 313 companies.
 *
 * TWO — A PROBE THAT RAN AND FOUND A BROKEN SOURCE IS NOT A PRODUCT FAILURE. The route answers 200
 * with `ok: false`, deliberately: *"turning it into a 5xx would make 'the customer's system is off'
 * indistinguishable from 'our product is broken'."* So a failed probe is rendered as a diagnosis,
 * with the failure KIND — never as an error state of this application.
 *
 * THREE — UNMAPPED IS A QUEUE, NOT A FAULT. 07 L-3: seven paid-for companies were skipped every night
 * for `no_link_for_raw_company` and nobody could see it, because the skip lived in a `_meta` array
 * inside a 47 MB JSON file. `unmappedCount` is that queue as a number on the response, and the words
 * for it are an invitation to work rather than a warning about a defect.
 *
 * FOUR — `not_found` COVERS TWO THINGS AND MUST KEEP COVERING THEM. The server collapses an unknown
 * connector and a cross-tenant one into one answer (S-12), because a caller who can tell them apart
 * can enumerate another office's connectors one probe at a time. This module renders the collapsed
 * answer and does NOT try to be more helpful than the server chose to be.
 *
 * ── AND NOTHING HERE NAMES A HOST, A PORT OR A SECRET ───────────────────────────────────────────
 *
 * 07 §2.3: an endpoint reference is a LOGICAL name — *"not a connection string, not a host literal"*
 * — and 07 N-6 forbids an adapter to hold a credential VALUE at all. The field names below are the
 * office's Hebrew for the reference fields; there is no key here whose value could be a credential,
 * and the diagnostic string that travels beside every failure is deliberately never rendered.
 *
 * Pure. Maps answers the server produced. Decides nothing, and computes no connection state.
 */

import type {
  BindingRefusal,
  BindingRefusalReason,
  ConnectorHealthState,
  ConnectorHealthSummary,
  DegradationCode,
  IdentifierConfidence,
  ProbeOutcome,
  SourceConfigurationRefusal,
  SourceDateFormat,
} from './contract';
import type { Tone } from './adminVocabulary';
import { formatInstantHe } from './adminVocabulary';
import { configurationFieldHe, missingConfigurationHe } from './sourceStatusText';

// ── The configuration form ────────────────────────────────────────────────────────────────────────

/**
 * THE FIELDS AS THE SCREEN KNOWS THEM, WHICH IS NOT AS THE WIRE KNOWS THEM.
 *
 * Each entry has a `formName` used for the DOM control and a `wireName` used to build the request
 * body. They differ on purpose: the wire names (`endpointRef`, `secretRef`) are the configuration
 * contract's own identifiers, and 09 §9.1 V3 allows exactly one technical string on a customer's
 * screen — a support reference. A `name="secretRef"` attribute in the markup would put a second one
 * there, in the one place a customer is most likely to copy it into a support email.
 *
 * `sourceSystemCode` and `displayNameHe` are the two the office actually authors; the other four are
 * references that a deployment resolves.
 */
export interface ConfigurationField {
  readonly formName: string;
  readonly wireName: string;
  readonly labelHe: string;
  readonly hintHe: string;
  /** `true` ⇒ the value is a REFERENCE that deployment configuration resolves, never a value. */
  readonly isReference: boolean;
}

export const CONFIGURATION_FIELDS: readonly ConfigurationField[] = [
  {
    formName: 'connection-name',
    wireName: 'displayNameHe',
    labelHe: 'שם החיבור במשרד',
    hintHe: 'השם שהמשרד קורא לחיבור הזה. אינו מגיע למערכת שבצד השני.',
    isReference: false,
  },
  {
    formName: 'source-system',
    wireName: 'sourceSystemCode',
    labelHe: 'סוג מערכת המקור',
    hintHe: 'איזו מערכת נמצאת בצד השני של החיבור.',
    isReference: false,
  },
  {
    formName: 'target-name',
    wireName: 'endpointRef',
    labelHe: 'שם היעד הלוגי',
    // 07 §2.3, said to the operator in their own words rather than as a rule number.
    hintHe: 'שם מוסכם ליעד. הכתובת עצמה נקבעת בהגדרות הפריסה ואינה נשמרת כאן.',
    isReference: true,
  },
  {
    formName: 'credential-name',
    wireName: 'secretRef',
    labelHe: 'הפניה לסוד ההזדהות',
    // 07 N-6 / PB5: a reference the broker resolves. The value never enters this application.
    hintHe: 'שם ההפניה בלבד. סיסמה, מפתח או מחרוזת התחברות אינם נשמרים במערכת בשום שלב.',
    isReference: true,
  },
  {
    formName: 'text-encoding',
    wireName: 'textEncoding',
    labelHe: 'קידוד התווים',
    hintHe: 'כיצד לפרש טקסט בעברית שמגיע מהמערכת הזו.',
    isReference: false,
  },
  {
    formName: 'date-format',
    wireName: 'sourceDateFormat',
    labelHe: 'מבנה התאריך במקור',
    hintHe: 'סדר היום, החודש והשנה כפי שהמערכת שבצד השני כותבת אותם.',
    isReference: false,
  },
];

/**
 * Builds the request body from the form's own field names.
 *
 * The mapping lives here, once, so that the DOM never has to carry a wire identifier and the screen
 * never has to know one. A field the form does not supply is simply absent, and the server's
 * `invalid_request` is the answer — this function invents no default.
 */
export function configurationBody(values: Readonly<Record<string, string>>): Record<string, string> {
  const body: Record<string, string> = {};
  for (const field of CONFIGURATION_FIELDS) {
    const value = values[field.formName];
    if (value !== undefined) body[field.wireName] = value;
  }
  return body;
}

// ── Configuration refusals ────────────────────────────────────────────────────────────────────────

export interface SourceRefusalText {
  readonly titleHe: string;
  readonly explanationHe: string;
  readonly nextStepHe: string;
  readonly tone: Tone;
}

const CONFIGURATION_REFUSAL_HE: Readonly<Record<string, SourceRefusalText>> = {
  connection_not_found: {
    titleHe: 'החיבור אינו זמין',
    explanationHe: 'לא הצלחנו להציג את החיבור שאליו מפנה הפעולה.',
    nextStepHe: 'חזרה לרשימת מקורות הנתונים ובחירת חיבור מתוכה.',
    tone: 'muted',
  },
  incomplete_configuration: {
    titleHe: 'ההגדרה לא נשמרה — חסרים פרטים',
    explanationHe:
      'החיבור נשאר במצב ״לא הוגדר״, ולא נשמרה הגדרה חלקית. כל הפרטים החסרים מפורטים כאן יחד, ' +
      'כדי שאפשר יהיה להשלים אותם בפעם אחת.',
    nextStepHe: 'השלמת כל הפרטים החסרים ושמירה מחדש.',
    tone: 'warn',
  },
  credential_value_rejected: {
    titleHe: 'הערך שהוזן נראה כמו סוד ולא כמו הפניה',
    explanationHe:
      'בשדות ההפניה נשמר שם בלבד. ערך שנראה כמו סיסמה, מפתח או מחרוזת התחברות נדחה לפני השמירה, ' +
      'ולכן לא נשמר דבר.',
    nextStepHe: 'הזנת שם ההפניה כפי שהוגדר בהגדרות הפריסה, במקום הערך עצמו.',
    tone: 'error',
  },
};

const GENERIC_SOURCE_REFUSAL: SourceRefusalText = {
  titleHe: 'הפעולה על החיבור לא בוצעה',
  explanationHe: 'השרת סירב לבקשה ולא נשמר שינוי בהגדרת החיבור.',
  nextStepHe: 'אפשר לרענן את מסך החיבור ולראות את מצבו העדכני לפני ניסיון נוסף.',
  tone: 'error',
};

/** Exposed for the mirror test only. */
export function mappedConfigurationRefusals(): readonly string[] {
  return Object.keys(CONFIGURATION_REFUSAL_HE);
}

/**
 * The configuration refusal, with the missing fields appended in the office's own words.
 *
 * `remedy` — the service's own sentence — is used ONLY where this build has no Hebrew for the reason,
 * and even then it is the fallback rather than the headline. It is written for an operator, and a
 * screen that led with it would be quoting the server at a bookkeeper.
 */
export function configurationRefusalText(refusal: SourceConfigurationRefusal): SourceRefusalText {
  const copy = CONFIGURATION_REFUSAL_HE[refusal.reason];
  const missing = missingConfigurationHe(refusal.missingConfiguration);

  if (copy === undefined) {
    return {
      ...GENERIC_SOURCE_REFUSAL,
      explanationHe:
        missing === ''
          ? GENERIC_SOURCE_REFUSAL.explanationHe
          : `${GENERIC_SOURCE_REFUSAL.explanationHe} ${missing}`,
    };
  }

  return {
    ...copy,
    explanationHe: missing === '' ? copy.explanationHe : `${copy.explanationHe} ${missing}`,
  };
}

/** Which configuration fields a refusal named, as form field names the screen can focus. */
export function missingFormFields(missing: readonly string[]): readonly string[] {
  const byWireName = new Map(CONFIGURATION_FIELDS.map((f) => [f.wireName, f.formName]));
  return missing.map((wire) => byWireName.get(wire)).filter((f): f is string => f !== undefined);
}

// ── The probe ─────────────────────────────────────────────────────────────────────────────────────

export interface ProbeText {
  readonly titleHe: string;
  readonly explanationHe: string;
  readonly nextStepHe: string;
  readonly tone: Tone;
  /**
   * `false` for EVERY probe answer, successful or not. A probe that ran is an answer about the
   * CUSTOMER'S system, and this application is not the thing that failed — 07 §3.3's stated gap with
   * a classification, rather than a 500.
   */
  readonly isOurFailure: false;
}

/**
 * The probe result. The failure sentence comes from `sourceStatusText.ts`'s failure table, which is
 * the same table the connection list uses — so a connection that says *"ההזדהות התקבלה, אך אין לה
 * הרשאה לנתוני החברה"* in the list says exactly that after a test, and not a second phrasing.
 */
export function probeText(
  outcome: ProbeOutcome,
  failureExplanationHe: (kind: ProbeFailureKind) => { readonly explanationHe: string; readonly nextStepHe: string },
): ProbeText {
  if (outcome.ok) {
    const at = formatInstantHe(outcome.checkedAtUtc);
    return {
      titleHe: 'הבדיקה עברה',
      explanationHe:
        at === null
          ? 'הצלחנו להגיע למערכת המקור ולקבל ממנה תשובה.'
          : `הצלחנו להגיע למערכת המקור ולקבל ממנה תשובה. הבדיקה בוצעה ב־${at}.`,
      // Said out loud, because a green test is exactly where a false claim of proven integration
      // starts: contact is not data, and the freshness line is the answer to whether numbers arrived.
      nextStepHe:
        'הבדיקה מעידה שהחיבור מגיע למערכת. היא אינה מעידה שהנתונים עדכניים — לכך יש להסתכל על שורת ' +
        'טריות הנתונים בכרטיס החיבור.',
      tone: 'ok',
      isOurFailure: false,
    };
  }

  const detail = failureExplanationHe(outcome.failureKind);
  return {
    titleHe: 'הבדיקה לא עברה',
    // "The check ran" is the first clause on purpose: it is what separates this from a broken product.
    explanationHe: `הבדיקה בוצעה והשיבה תשובה. ${detail.explanationHe}`,
    nextStepHe: detail.nextStepHe,
    tone: 'error',
    isOurFailure: false,
  };
}

/** Narrower alias so this module does not re-declare the failure union it borrows. */
export type ProbeFailureKind = Extract<ProbeOutcome, { ok: false }>['failureKind'];

// ── Discovery and mapping ─────────────────────────────────────────────────────────────────────────

/**
 * The unmapped queue, in words. 07 L-3.
 *
 * The number is the SERVER'S count over the whole set, not the length of whatever page the screen is
 * showing — 09 §5.1's "313 is not 313": a figure of the form "N companies" states which N it is.
 */
export function unmappedQueueHe(unmappedCount: number, total: number): string {
  if (unmappedCount === 0) {
    return `כל ${total} החברות שבמערכת המקור מקושרות לחברה במשרד.`;
  }
  return (
    `${unmappedCount} מתוך ${total} החברות שבמערכת המקור אינן מקושרות לאף חברה במשרד. ` +
    'נתונים מהחברות האלה אינם נקלטים כלל, והקישור נעשה ידנית — המערכת אינה מקשרת חברה מעצמה.'
  );
}

const BINDING_REFUSAL_HE: Readonly<Record<BindingRefusalReason, SourceRefusalText>> = {
  /**
   * The collapsed answer, and it stays collapsed. It covers an unknown connector AND one belonging to
   * another office, and the sentence is written so that neither can be inferred from it.
   */
  not_found: {
    titleHe: 'לא נמצא קישור למזהה הזה',
    explanationHe: 'המזהה שהוזן אינו מוכר לחיבור הזה.',
    nextStepHe: 'בדיקת המזהה מול רשימת החברות שבמערכת המקור, ובחירה מתוכה.',
    tone: 'muted',
  },
  unmapped: {
    titleHe: 'החברה במערכת המקור אינה מקושרת',
    explanationHe:
      'החברה קיימת במערכת המקור ואינה מקושרת לאף חברה במשרד, ולכן הנתונים שלה אינם נקלטים.',
    nextStepHe: 'בחירת החברה במשרד שאליה יש לקשר אותה.',
    tone: 'warn',
  },
  ambiguous: {
    titleHe: 'יותר מחברה אחת במשרד תובעת את המזהה הזה',
    explanationHe:
      'הקישור לא נקבע, כי יותר מחברה אחת במשרד מקושרת לאותו מזהה במערכת המקור. ' +
      'המערכת אינה בוחרת ביניהן — בחירה שגויה כאן מכניסה נתונים של לקוח אחד לתיק של אחר.',
    nextStepHe: 'הכרעה איזו חברה היא הנכונה, והסרת הקישור מן השאר.',
    tone: 'error',
  },
  unavailable: {
    titleHe: 'לא ניתן לבדוק את הקישור כרגע',
    explanationHe: 'לא הצלחנו לפנות למערכת המקור, ולכן אין תשובה על הקישור.',
    nextStepHe: 'הרצת בדיקת חיבור, ולאחריה ניסיון נוסף.',
    tone: 'warn',
  },
};

/** Exposed for the mirror test only. */
export function mappedBindingRefusals(): readonly string[] {
  return Object.keys(BINDING_REFUSAL_HE);
}

export interface BindingRefusalText extends SourceRefusalText {
  /** How many internal companies claim it. Never WHICH — those are another office's ids to nobody. */
  readonly candidatesHe: string | null;
  /** 0002 rule 3 — what would settle it. `null` where the server sent none. */
  readonly resolvingFactHe: string | null;
}

export function bindingRefusalText(refusal: BindingRefusal): BindingRefusalText {
  const copy = BINDING_REFUSAL_HE[refusal.reason];
  return {
    ...copy,
    candidatesHe:
      refusal.candidateCount > 1 ? `מספר החברות שתובעות את המזהה: ${refusal.candidateCount}.` : null,
    resolvingFactHe:
      refusal.resolvingFactRequired === null
        ? null
        : `מה שיכריע: ${refusal.resolvingFactRequired}`,
  };
}

/**
 * The two closed lists a mapping form has to offer, in office Hebrew.
 *
 * The confidence list is NOT a slider and not a percentage: 05 §4 makes an identifier an ASSERTION
 * with a stated strength, and `RESOLVABLE_CONFIDENCES` on the server decides which strengths may be
 * acted on. A screen that offered "80%" would be inventing a seventh member of a closed enum.
 */
const DATE_FORMAT_HE: Readonly<Record<SourceDateFormat, string>> = {
  ISO_8601: 'שנה-חודש-יום',
  DAY_FIRST_SLASH: 'יום/חודש/שנה',
  COMPACT_YYYYMMDD: 'שנהחודשיום ברצף, ללא מפרידים',
};

export function sourceDateFormatHe(format: SourceDateFormat): string {
  return DATE_FORMAT_HE[format];
}

const CONFIDENCE_HE: Readonly<Record<IdentifierConfidence, string>> = {
  authoritative: 'מקור רשמי — אומת מול רשות',
  human_confirmed: 'אושר בידי אדם במשרד',
  derived_verified: 'נגזר ואומת מנתונים קיימים',
  legacy_unverified: 'הועבר ממערכת קודמת ולא אומת',
  proposed: 'הצעה שטרם אושרה',
  disputed: 'שנוי במחלוקת',
};

export function identifierConfidenceHe(confidence: IdentifierConfidence): string {
  return CONFIDENCE_HE[confidence];
}

/** Exposed for the mirror test only. */
export function mappedDateFormats(): readonly string[] {
  return Object.keys(DATE_FORMAT_HE);
}

/** Exposed for the mirror test only. */
export function mappedConfidences(): readonly string[] {
  return Object.keys(CONFIDENCE_HE);
}

// ── Connector health ──────────────────────────────────────────────────────────────────────────────

/**
 * `NEVER_CONNECTED` IS NOT A KIND OF `DISCONNECTED`, and this table is where that stops being a
 * comment. The first is an installation problem on the customer's machine — egress, agent start-up,
 * the wrong build; the second worked yesterday and is an operations problem. Same colour on a
 * dashboard, different person, different hour.
 */
const HEALTH_HE: Readonly<Record<ConnectorHealthState, SourceRefusalText>> = {
  NEVER_CONNECTED: {
    titleHe: 'מעולם לא נוצר קשר',
    explanationHe: 'תוכנת החיבור נרשמה, ומעולם לא יצרה קשר עם המערכת. אין כאן תקלה שנוצרה — יש התקנה שלא הושלמה.',
    nextStepHe: 'בדיקה שתוכנת החיבור הותקנה ופועלת אצל הלקוח.',
    tone: 'muted',
  },
  UNMEASURABLE: {
    titleHe: 'אין בסיס לקביעת מצב הקשר',
    explanationHe: 'לא הוגדר לוח זמנים לתוכנת החיבור, ולכן אין ממה לקבוע אם היא בקשר או לא.',
    nextStepHe: 'הגדרת לוח זמנים לתוכנת החיבור, כדי שהמצב יהיה ניתן למדידה.',
    tone: 'warn',
  },
  CONNECTED: {
    titleHe: 'בקשר',
    explanationHe: 'תוכנת החיבור מדווחת במועד.',
    nextStepHe: 'אין פעולה נדרשת.',
    tone: 'ok',
  },
  DEGRADED: {
    titleHe: 'בקשר, עם בעיות',
    explanationHe: 'תוכנת החיבור מדווחת, ויש בעיות שידרשו טיפול לפני שיהפכו לניתוק.',
    nextStepHe: 'טיפול בבעיות המפורטות כאן לפני שהן עוצרות את החיבור.',
    tone: 'warn',
  },
  DISCONNECTED: {
    titleHe: 'הקשר נפסק',
    explanationHe: 'תוכנת החיבור דיווחה בעבר והפסיקה. זהו שינוי ממצב עובד, ולא התקנה שלא הושלמה.',
    nextStepHe: 'בדיקה מדוע תוכנת החיבור הפסיקה לדווח אצל הלקוח.',
    tone: 'error',
  },
};

/** Exposed for the mirror test only. */
export function mappedHealthStates(): readonly string[] {
  return Object.keys(HEALTH_HE);
}

/**
 * The degradations, each one a thing a person fixes. They are carried even on `DISCONNECTED`, because
 * they are still what somebody has to repair after the link comes back.
 */
const DEGRADATION_HE: Readonly<Record<DegradationCode, string>> = {
  CREDENTIAL_ROTATION_DUE: 'הגיע מועד להחלפת פרטי ההזדהות של החיבור.',
  PROTOCOL_VERSION_SUNSETTING: 'גרסת תוכנת החיבור עומדת לצאת משימוש.',
  CLOCK_SKEW_NEAR_WINDOW: 'השעון של תוכנת החיבור מתרחק מהשעון של המערכת. כשיחרוג, כל הודעה תידחה.',
  SOURCE_UNREACHABLE: 'תוכנת החיבור אינה מצליחה להגיע למערכת המקור.',
  SOURCE_REFUSING_REQUESTS: 'מערכת המקור מסרבת לפניות של תוכנת החיבור.',
  BINDINGS_UNMAPPED: 'יש חברות במערכת המקור שאינן מקושרות, והנתונים שלהן אינם נקלטים.',
};

export function degradationHe(code: DegradationCode): string {
  return DEGRADATION_HE[code];
}

/** Exposed for the mirror test only. */
export function mappedDegradationCodes(): readonly string[] {
  return Object.keys(DEGRADATION_HE);
}

export interface HealthText extends SourceRefusalText {
  readonly lastSeenHe: string | null;
  readonly degradationsHe: readonly string[];
  /**
   * ALWAYS PRESENT. `contactIsNotDataFreshness` is on the payload so a consumer cannot claim it was
   * not told, and this is the sentence that tells the person. A green connector above a stale trial
   * balance is the June 2026 freeze exactly.
   */
  readonly contactIsNotFreshnessHe: string;
}

export function healthText(health: ConnectorHealthSummary): HealthText {
  const copy = HEALTH_HE[health.state];
  const lastSeen = formatInstantHe(health.lastSeenAtUtc);
  return {
    ...copy,
    lastSeenHe: lastSeen === null ? null : `הדיווח האחרון מתוכנת החיבור: ${lastSeen}`,
    degradationsHe: health.degradations.map(degradationHe),
    contactIsNotFreshnessHe:
      'מצב הקשר אינו אומר דבר על טריות הנתונים. חיבור שמדווח במועד יכול להביא נתונים ישנים.',
  };
}

/** The configuration field names, in the office's Hebrew. Re-exported so a screen imports one module. */
export { configurationFieldHe };
